import { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { startRegistration } from '@simplewebauthn/browser';
import { Smartphone, KeyRound, Trash2, Copy, Check } from 'lucide-react';
import { useI18n, useNamespace } from '../context/I18nContext';
import { useToast } from '../context/ToastContext';
import Modal from '../shared/components/Modal';
import { apiClient } from '../shared/lib/apiClient';

// Maps backend MFA error codes to a specific message key, same pattern as
// LoginPage's ERROR_CODE_TO_MESSAGE_KEY -- most of these codes are precise
// enough to surface directly (e.g. "that code is wrong" vs. "your session
// expired") without any credential-enumeration concern, since this page is
// only reachable by an already-authenticated user managing their own MFA.
const MFA_ERROR_CODE_TO_MESSAGE_KEY = {
  NO_PENDING_ENROLLMENT: 'errors.noPendingEnrollment',
  INVALID_CODE: 'errors.invalidCode',
  VALIDATION_ERROR: 'errors.validationError',
  MFA_NOT_ENABLED: 'errors.mfaNotEnabled',
  CHALLENGE_EXPIRED: 'errors.challengeExpired',
  WEBAUTHN_VERIFICATION_FAILED: 'errors.webauthnVerificationFailed',
  NOT_FOUND: 'errors.notFound',
  NETWORK_ERROR: 'errors.network',
};

function mfaErrorMessageKey(err) {
  return MFA_ERROR_CODE_TO_MESSAGE_KEY[err?.code] || 'errors.generic';
}

const CHANGE_PASSWORD_ERROR_CODE_TO_MESSAGE_KEY = {
  INVALID_CURRENT_PASSWORD: 'changePassword.errorInvalidCurrentPassword',
  PASSWORD_REUSED: 'changePassword.errorPasswordReused',
  PASSWORD_PWNED: 'changePassword.errorPasswordPwned',
  RATE_LIMITED: 'changePassword.errorRateLimited',
  NETWORK_ERROR: 'errors.network',
};

function changePasswordErrorMessageKey(err) {
  return CHANGE_PASSWORD_ERROR_CODE_TO_MESSAGE_KEY[err?.code] || 'errors.generic';
}

// Must match apps/api/src/modules/core/routes.js's
// `newPassword: { minLength: 10 }` validation rule.
const MIN_PASSWORD_LENGTH = 10;

const EMPTY_TOTP_SETUP = { secret: '', otpauthUri: '', qrDataUrl: '', code: '', submitting: false, error: null };

/**
 * Self-service MFA management: enrolled methods (TOTP + WebAuthn), enrollment
 * flows for both, and backup-code generation. Mirrors the detail-page/record-list
 * conventions used by CustomerDetailPage/CustomerContacts (Modal for
 * add/confirm flows, .record-list for the methods list, .error-banner for
 * API errors) rather than introducing a new page layout pattern.
 */
export default function SecuritySettingsPage() {
  useNamespace('security');
  const { t, language } = useI18n();
  const { showToast } = useToast();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState(null);

  const [totpStarting, setTotpStarting] = useState(false);
  const [totpSetup, setTotpSetup] = useState(null); // { secret, otpauthUri, qrDataUrl, code, submitting, error }
  const [secretCopied, setSecretCopied] = useState(false);

  const [webauthnModal, setWebauthnModal] = useState(null); // { nickname, submitting, error }

  const [pendingRemove, setPendingRemove] = useState(null); // method being removed
  const [removing, setRemoving] = useState(false);

  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [backupCodes, setBackupCodes] = useState(null); // string[] | null -- shown once
  const [allCodesCopied, setAllCodesCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const res = await apiClient.get('/auth/mfa/status');
      setStatus(res.data);
    } catch (err) {
      setPageError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const formatDate = (value) => {
    if (!value) return t('security', 'methods.neverUsed');
    return new Date(value).toLocaleString(language);
  };

  const methodLabel = (method) => {
    if (method.nickname) return method.nickname;
    return t('security', method.type === 'totp' ? 'methods.totpDefaultName' : 'methods.webauthnDefaultName');
  };

  // ---- Change password ----------------------------------------------------
  const handleChangePassword = async (event) => {
    event.preventDefault();
    setChangePasswordError(null);

    // Checked client-side first, same reasoning as PasswordResetConfirmPage:
    // this is the common, easy-to-hit case and shouldn't need a round trip.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setChangePasswordError({ message: t('security', 'changePassword.errorPasswordTooShort', { count: MIN_PASSWORD_LENGTH }) });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError({ message: t('security', 'changePassword.errorPasswordsMustMatch') });
      return;
    }

    setChangingPassword(true);
    try {
      await apiClient.post('/auth/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      showToast(t('security', 'changePassword.successMessage'));
    } catch (err) {
      setChangePasswordError({ message: t('security', changePasswordErrorMessageKey(err)) });
    } finally {
      setChangingPassword(false);
    }
  };

  // ---- TOTP enrollment ----------------------------------------------------
  const startTotpSetup = async () => {
    setTotpStarting(true);
    setPageError(null);
    try {
      const res = await apiClient.post('/auth/mfa/totp/setup');
      const qrDataUrl = await QRCode.toDataURL(res.data.otpauthUri);
      setTotpSetup({ ...EMPTY_TOTP_SETUP, secret: res.data.secret, otpauthUri: res.data.otpauthUri, qrDataUrl });
    } catch (err) {
      setPageError(err);
    } finally {
      setTotpStarting(false);
    }
  };

  const cancelTotpSetup = () => {
    setTotpSetup(null);
    setSecretCopied(false);
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(totpSetup.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    } catch (e) {
      // Clipboard access can fail silently (e.g. insecure context) -- the
      // secret is still shown as plain text for manual copying.
    }
  };

  const confirmTotpSetup = async (event) => {
    event.preventDefault();
    setTotpSetup((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const res = await apiClient.post('/auth/mfa/totp/confirm', { code: totpSetup.code.trim() });
      setTotpSetup(null);
      setSecretCopied(false);
      if (res.data.backupCodes) setBackupCodes(res.data.backupCodes);
      await loadStatus();
    } catch (err) {
      setTotpSetup((prev) => ({ ...prev, submitting: false, error: err }));
    }
  };

  // ---- WebAuthn enrollment --------------------------------------------------
  const openWebauthnModal = () => setWebauthnModal({ nickname: '', submitting: false, error: null });
  const cancelWebauthnModal = () => setWebauthnModal(null);

  const startWebauthnRegistration = async (event) => {
    event.preventDefault();
    setWebauthnModal((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const optionsRes = await apiClient.post('/auth/mfa/webauthn/register-options');

      let credential;
      try {
        credential = await startRegistration({ optionsJSON: optionsRes.data });
      } catch (browserError) {
        // startRegistration() throws a plain browser exception (e.g. the
        // user cancelled the OS prompt, or the device/browser doesn't
        // support a passkey) -- this isn't an ApiError, so it's handled
        // separately from the mfaErrorMessageKey mapping below.
        setWebauthnModal((prev) => ({ ...prev, submitting: false, error: { deviceError: true } }));
        return;
      }

      const nickname = webauthnModal.nickname.trim();
      const verifyRes = await apiClient.post('/auth/mfa/webauthn/register-verify', {
        response: credential,
        ...(nickname ? { nickname } : {}),
      });
      setWebauthnModal(null);
      if (verifyRes.data.backupCodes) setBackupCodes(verifyRes.data.backupCodes);
      await loadStatus();
    } catch (err) {
      setWebauthnModal((prev) => ({ ...prev, submitting: false, error: err }));
    }
  };

  // ---- Remove method ----------------------------------------------------
  const confirmRemoveMethod = async () => {
    setRemoving(true);
    try {
      await apiClient.delete(`/auth/mfa/methods/${pendingRemove.id}`);
      setPendingRemove(null);
      await loadStatus();
    } catch (err) {
      setPendingRemove(null);
      setPageError(err);
    } finally {
      setRemoving(false);
    }
  };

  // ---- Backup codes regeneration -----------------------------------------
  const regenerateBackupCodes = async () => {
    setRegenerating(true);
    try {
      const res = await apiClient.post('/auth/mfa/backup-codes/regenerate');
      setConfirmingRegenerate(false);
      setAllCodesCopied(false);
      setBackupCodes(res.data.backupCodes);
      await loadStatus();
    } catch (err) {
      setConfirmingRegenerate(false);
      setPageError(err);
    } finally {
      setRegenerating(false);
    }
  };

  const copyAllBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setAllCodesCopied(true);
      setTimeout(() => setAllCodesCopied(false), 1500);
    } catch (e) {
      // ignore -- codes remain visible for manual copying
    }
  };

  const dismissBackupCodes = () => {
    setBackupCodes(null);
    setAllCodesCopied(false);
  };

  const methods = status?.methods || [];
  const mfaEnabled = Boolean(status?.mfaEnabled);

  return (
    <div className="page">
      <div className="page__header">
        <h1 className="page__title">{t('security', 'title')}</h1>
        {!loading && status && (
          <span className={`status-badge${mfaEnabled ? ' status-badge--active' : ''}`}>
            {t('security', mfaEnabled ? 'status.enabled' : 'status.disabled')}
          </span>
        )}
      </div>

      <p className="record-list__subtitle">{t('security', 'description')}</p>

      {pageError && <div className="error-banner">{t('security', mfaErrorMessageKey(pageError))}</div>}

      {loading ? (
        <div className="empty-state">{t('common', 'actions.loading')}</div>
      ) : (
        <>
          <section className="tab-panel-content">
            <h2 className="security-section-title">{t('security', 'changePassword.sectionTitle')}</h2>

            {changePasswordError && <div className="error-banner">{changePasswordError.message}</div>}

            <form onSubmit={handleChangePassword}>
              <div className="form-field">
                <label className="form-label" htmlFor="change-password-current">
                  {t('security', 'changePassword.currentPasswordLabel')}
                </label>
                <input
                  id="change-password-current"
                  className="form-input"
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="form-field">
                <label className="form-label" htmlFor="change-password-new">
                  {t('security', 'changePassword.newPasswordLabel')}
                </label>
                <input
                  id="change-password-new"
                  className="form-input"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                />
                <span className="form-hint">{t('security', 'changePassword.passwordHint', { count: MIN_PASSWORD_LENGTH })}</span>
              </div>
              <div className="form-field">
                <label className="form-label" htmlFor="change-password-confirm">
                  {t('security', 'changePassword.confirmPasswordLabel')}
                </label>
                <input
                  id="change-password-confirm"
                  className="form-input"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(event) => setConfirmNewPassword(event.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={changingPassword}>
                  {t('security', 'changePassword.submitButton')}
                </button>
              </div>
            </form>
          </section>

          <section className="tab-panel-content">
            <h2 className="security-section-title">{t('security', 'methods.sectionTitle')}</h2>
            <div className="tab-panel-toolbar">
              <button type="button" className="btn btn-primary" onClick={startTotpSetup} disabled={totpStarting}>
                <Smartphone size={16} /> {t('security', 'methods.addTotpButton')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={openWebauthnModal}>
                <KeyRound size={16} /> {t('security', 'methods.addWebauthnButton')}
              </button>
            </div>

            {methods.length === 0 ? (
              <p className="empty-state">{t('security', 'methods.emptyState')}</p>
            ) : (
              <ul className="record-list">
                {methods.map((method) => (
                  <li key={method.id} className="record-list__item">
                    <div className="record-list__main">
                      <span className="record-list__title">
                        {method.type === 'totp' ? <Smartphone size={16} /> : <KeyRound size={16} />}
                        {methodLabel(method)}
                      </span>
                      <span className="record-list__meta">
                        {t('security', 'methods.added', { date: formatDate(method.createdAt) })}
                      </span>
                      <span className="record-list__meta">
                        {t('security', 'methods.lastUsed', { date: formatDate(method.lastUsedAt) })}
                      </span>
                    </div>
                    <div className="record-list__actions">
                      <button
                        type="button"
                        className="btn-icon-plain btn-icon-plain--danger"
                        onClick={() => setPendingRemove(method)}
                        aria-label={t('security', 'methods.remove')}
                        title={t('security', 'methods.remove')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="tab-panel-content">
            <h2 className="security-section-title">{t('security', 'backupCodes.title')}</h2>
            <p className="record-list__subtitle">
              {mfaEnabled
                ? t('security', 'backupCodes.remaining', { count: status.backupCodesRemaining })
                : t('security', 'backupCodes.requiresMfa')}
            </p>
            <div className="tab-panel-toolbar">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmingRegenerate(true)}
                disabled={!mfaEnabled}
              >
                {t('security', 'backupCodes.regenerateButton')}
              </button>
            </div>
          </section>
        </>
      )}

      {totpSetup && (
        <Modal
          title={t('security', 'totp.setupTitle')}
          onClose={cancelTotpSetup}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={cancelTotpSetup} disabled={totpSetup.submitting}>
                {t('common', 'actions.cancel')}
              </button>
              <button
                type="submit"
                form="totp-confirm-form"
                className="btn btn-primary"
                disabled={totpSetup.submitting || totpSetup.code.trim().length === 0}
              >
                {t('security', 'totp.confirmButton')}
              </button>
            </>
          }
        >
          {totpSetup.error && <div className="error-banner">{t('security', mfaErrorMessageKey(totpSetup.error))}</div>}
          <p className="form-hint">{t('security', 'totp.instructions')}</p>
          <div className="security-qr">
            <img className="security-qr__image" src={totpSetup.qrDataUrl} alt={t('security', 'totp.qrAlt')} />
            <div>
              <p className="form-label">{t('security', 'totp.secretLabel')}</p>
              <div className="security-secret">
                <span className="security-secret__value">{totpSetup.secret}</span>
                <button
                  type="button"
                  className="btn-icon-plain"
                  onClick={copySecret}
                  aria-label={t('security', secretCopied ? 'totp.secretCopied' : 'totp.copySecret')}
                  title={t('security', secretCopied ? 'totp.secretCopied' : 'totp.copySecret')}
                >
                  {secretCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          </div>
          <form id="totp-confirm-form" onSubmit={confirmTotpSetup}>
            <div className="form-field">
              <label className="form-label" htmlFor="totp-code">
                {t('security', 'totp.codeLabel')}
              </label>
              <input
                id="totp-code"
                className="form-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={totpSetup.code}
                placeholder={t('security', 'totp.codePlaceholder')}
                onChange={(event) => setTotpSetup((prev) => ({ ...prev, code: event.target.value }))}
                required
              />
            </div>
          </form>
        </Modal>
      )}

      {webauthnModal && (
        <Modal
          title={t('security', 'webauthn.setupTitle')}
          onClose={cancelWebauthnModal}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={cancelWebauthnModal} disabled={webauthnModal.submitting}>
                {t('common', 'actions.cancel')}
              </button>
              <button type="submit" form="webauthn-form" className="btn btn-primary" disabled={webauthnModal.submitting}>
                {t('security', 'webauthn.registerButton')}
              </button>
            </>
          }
        >
          {webauthnModal.error && (
            <div className="error-banner">
              {webauthnModal.error.deviceError
                ? t('security', 'errors.webauthnDeviceFailed')
                : t('security', mfaErrorMessageKey(webauthnModal.error))}
            </div>
          )}
          <p className="form-hint">{t('security', 'webauthn.instructions')}</p>
          <form id="webauthn-form" onSubmit={startWebauthnRegistration}>
            <div className="form-field">
              <label className="form-label" htmlFor="webauthn-nickname">
                {t('security', 'webauthn.nicknameLabel')}
              </label>
              <input
                id="webauthn-nickname"
                className="form-input"
                type="text"
                value={webauthnModal.nickname}
                placeholder={t('security', 'webauthn.nicknamePlaceholder')}
                onChange={(event) => setWebauthnModal((prev) => ({ ...prev, nickname: event.target.value }))}
              />
            </div>
          </form>
        </Modal>
      )}

      {pendingRemove && (
        <Modal
          title={t('security', 'methods.removeConfirmTitle')}
          onClose={() => setPendingRemove(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPendingRemove(null)} disabled={removing}>
                {t('common', 'actions.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmRemoveMethod} disabled={removing}>
                {t('common', 'actions.delete')}
              </button>
            </>
          }
        >
          <p>{t('security', 'methods.removeConfirmMessage', { name: methodLabel(pendingRemove) })}</p>
        </Modal>
      )}

      {confirmingRegenerate && (
        <Modal
          title={t('security', 'backupCodes.regenerateConfirmTitle')}
          onClose={() => setConfirmingRegenerate(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmingRegenerate(false)}
                disabled={regenerating}
              >
                {t('common', 'actions.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={regenerateBackupCodes} disabled={regenerating}>
                {t('security', 'backupCodes.regenerateButton')}
              </button>
            </>
          }
        >
          <p>{t('security', 'backupCodes.regenerateConfirmMessage')}</p>
        </Modal>
      )}

      {backupCodes && (
        <Modal
          title={t('security', 'backupCodes.showTitle')}
          onClose={() => {}}
          footer={
            <button type="button" className="btn btn-primary" onClick={dismissBackupCodes}>
              {t('security', 'backupCodes.acknowledgeButton')}
            </button>
          }
        >
          <p className="form-hint">{t('security', 'backupCodes.showWarning')}</p>
          <div className="security-backup-codes">
            {backupCodes.map((code) => (
              <span key={code} className="security-backup-codes__code">
                {code}
              </span>
            ))}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={copyAllBackupCodes}>
              {allCodesCopied ? <Check size={16} /> : <Copy size={16} />}
              {t('security', allCodesCopied ? 'backupCodes.copiedAll' : 'backupCodes.copyAllButton')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
